import { useEffect, useState } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Sparkles,
  Send,
  Receipt,
  CreditCard,
  Calendar,
  Tag,
  Copy,
  Search,
  AlertCircle,
  TrendingUp,
  Scale,
  AlertTriangle,
  UserPlus,
  FileText,
  List,
  PenTool,
  Save,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map of action-icon strings (returned by backend) to lucide components.
// Keeps the icon set explicit so backend can safely add new ones.
const ICONS = {
  sparkles: Sparkles,
  send: Send,
  receipt: Receipt,
  'credit-card': CreditCard,
  calendar: Calendar,
  tag: Tag,
  copy: Copy,
  search: Search,
  'alert-circle': AlertCircle,
  'trending-up': TrendingUp,
  scale: Scale,
  'alert-triangle': AlertTriangle,
  'user-plus': UserPlus,
  'file-text': FileText,
  list: List,
  'pen-tool': PenTool,
  save: Save,
};

/**
 * Smart Actions bar — fetches `/api/ocr/{jobId}/smart-actions` and renders
 * a row of pill buttons that map to AI-suggested next steps for the
 * detected document type (bill, invoice, tax return, etc.).
 *
 * Phase 2 of the new OCR experience: gives users a magical "what would
 * you like to do with this?" prompt right after AI Buck finishes reading.
 */
export default function SmartActionsBar({ jobId, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!jobId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/smart-actions`);
        if (!r.ok) throw new Error('failed');
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loading || !data || !data.actions || data.actions.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30"
      data-testid="smart-actions-bar"
    >
      <div className="flex items-center gap-2 mr-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <span className="text-xs font-medium text-violet-900 dark:text-violet-200">
          AI Buck thinks this is a
        </span>
        <Badge variant="secondary" className="bg-violet-200 text-violet-900 dark:bg-violet-900 dark:text-violet-100">
          {data.doc_type_label || data.doc_type}
        </Badge>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          — try:
        </span>
      </div>
      {data.actions.map((a) => {
        const Icon = ICONS[a.icon] || Sparkles;
        return (
          <Button
            key={a.id}
            variant="outline"
            size="sm"
            className="h-8 rounded-full bg-white hover:bg-violet-50 dark:bg-slate-900 dark:hover:bg-violet-950/40"
            onClick={() => onAction && onAction(a, data)}
            data-testid={`smart-action-${a.id}`}
          >
            <Icon className="w-3.5 h-3.5 mr-1.5" />
            {a.label}
          </Button>
        );
      })}
    </div>
  );
}
